import { t as localeText } from "@/i18n/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import type { ThreeDAsset } from "./threeDState";
import { readThreeDModel } from "./threeDAssets";
import { ThreeDModelError } from "./viewer/modelPolicy";
import { Button, EmptyState, TextLink } from "@/shared/ui";
import "./viewer/ThreeDViewer.css";

const ThreeDCanvas = lazy(() => import("./viewer/ThreeDCanvas").then(module => ({ default: module.ThreeDCanvas })));
export interface ThreeDViewerProps { asset: ThreeDAsset; active?: boolean; className?: string }

/** GPU code is loaded only for an active, custody-verified original. */
export function ThreeDViewer({ asset, active = true, className = "" }: ThreeDViewerProps) {
  useLocaleSubscription();
  return <div className={`three-d-viewer ${className}`}>
    {active ? <ActiveModel key={`${asset.space_id}:${asset.id}:${asset.version}:${asset.integrity_digest}`} asset={asset} /> : <div className="three-d-viewer-state">{localeText("interface.model_preview_paused_5d88db33", { defaultValue: "Model preview paused" })}</div>}
  </div>;
}

function ActiveModel({ asset }: { asset: ThreeDAsset }) {
  useLocaleSubscription();
  const [attempt, setAttempt] = useState(0);
  const [bytes, setBytes] = useState<ArrayBuffer>();
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setBytes(undefined); setError("");
    const timeout = window.setTimeout(() => {
      setError(localeText("interface.the_model_download_took_too_long_try_again_or_op_81d7aa3b", { defaultValue: "The model download took too long. Try again or open the original in Assets." }));
      controller.abort();
    }, 60_000);
    void readThreeDModel(asset, controller.signal).then(data => {
      if (!controller.signal.aborted) setBytes(data);
    }).catch((reason: unknown) => {
      if (!controller.signal.aborted) setError(reason instanceof ThreeDModelError ? reason.message : localeText("interface.the_private_model_couldn_t_load_try_again_or_ope_33c1bda5", { defaultValue: "The private model couldn’t load. Try again or open its original in Assets." }));
    }).finally(() => window.clearTimeout(timeout));
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [asset, attempt]);
  if (error) return <div className="three-d-viewer-state"><EmptyState state="error" title={localeText("interface.model_preview_unavailable_ce2b6640", { defaultValue: "Model preview unavailable" })} description={error} action={<div className="flex flex-wrap justify-center gap-3"><Button variant="outline" onClick={() => setAttempt(value => value + 1)}>{localeText("interface.try_preview_again_55e6067d", { defaultValue: "Try preview again" })}</Button><TextLink href={`/assets?asset=${encodeURIComponent(asset.id)}`}>{localeText("interface.open_model_in_assets_62e20825", { defaultValue: "Open model in Assets" })}</TextLink></div>} /></div>;
  const pending = <div className="three-d-viewer-state" role="status">{localeText("interface.loading_verified_model_63b4c9f9", { defaultValue: "Loading verified model…" })}</div>;
  return bytes ? <ViewerBoundary onError={setError}><Suspense fallback={pending}><ThreeDCanvas data={bytes} name={asset.name} onError={setError} /></Suspense></ViewerBoundary> : pending;
}

class ViewerBoundary extends Component<{ children: ReactNode; onError: (message: string) => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError("The 3D viewer couldn’t load. Try again or open the original in Assets."); }
  render() { return this.state.failed ? null : this.props.children; }
}
